import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, ArrowRight, ExternalLink, Mail, CheckCircle2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";

const DEV_MODE = String(process.env.REACT_APP_AUTH_DEV_MODE || "").toLowerCase() === "true";

export default function Login() {
    const {
        user, loginWithPassword,
        requestMagicLink, requestPasswordReset, verifyToken,
        signInWithGoogle,
    } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotDevLink, setForgotDevLink] = useState(null);
    const [magicDevLink, setMagicDevLink] = useState(null);

    const destFor = (u) =>
        params.get("next") ||
        (["master_admin", "admin"].includes(u?.role)
            ? "/admin"
            : u?.role === "agent"
                ? "/my"
                : "/jarvis");

    if (user) {
        setTimeout(() => navigate(destFor(user), { replace: true }), 0);
    }

    const handleGoogle = async () => {
        setGoogleLoading(true);
        try {
            await signInWithGoogle({ redirectTo: params.get("next") || "/jarvis" });
        } catch (err) {
            setGoogleLoading(false);
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Couldn't start Google sign-in.",
            );
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) return;
        setLoading(true);
        try {
            const u = await loginWithPassword(email.trim(), password);
            toast.success(`Welcome, ${u.name || u.email}`);
            navigate(destFor(u), { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data || {};
            if (status === 423) {
                const mins = Math.ceil((data.retry_after_seconds || 0) / 60);
                toast.error(`Account temporarily locked. Try again in ${mins} ${mins === 1 ? "minute" : "minutes"}.`);
            } else if (status === 401) {
                toast.error("Invalid email or password.");
            } else {
                toast.error(formatApiErrorDetail(data.detail) || "Sign-in failed.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgot = async (e) => {
        e?.preventDefault?.();
        if (!email.trim()) {
            toast.error("Enter your email above first.");
            return;
        }
        setLoading(true);
        try {
            const res = await requestPasswordReset(email.trim());
            setForgotSent(true);
            setForgotDevLink(res?.dev_link || null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send reset link.");
        } finally {
            setLoading(false);
        }
    };

    const handleDevMagic = async () => {
        if (!email.trim()) { toast.error("Enter email first."); return; }
        setLoading(true);
        try {
            const res = await requestMagicLink(email.trim());
            setMagicDevLink(res?.dev_link || null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send magic link.");
        } finally {
            setLoading(false);
        }
    };

    const applyMagicLink = async (link) => {
        try {
            setLoading(true);
            const url = new URL(link);
            const token = url.searchParams.get("token");
            const u = await verifyToken(token);
            toast.success(`Welcome, ${u.name || u.email}`);
            navigate(destFor(u), { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verify failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-mm-bg px-5 py-10" data-testid="login-page">
            <div className="w-full max-w-5xl">
                <div className="mb-8 flex justify-center">
                    <BrandLogo size={40} />
                </div>
                <div className="grid gap-6 md:grid-cols-2 md:items-stretch">
                    {/* Left — existing password sign-in (unchanged behavior) */}
                    <div className="mm-card p-8" data-testid="login-password-card">
                    <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mm-navy/10 text-mm-navy">
                        <Lock size={18} />
                    </div>
                    <h1 className="mm-display text-2xl text-mm-navy">Sign in to Moving Mountains.</h1>
                    <p className="mt-2 text-sm text-mm-muted">
                        Use your email and password.
                    </p>

                    <form onSubmit={handleLogin} className="mt-6 space-y-4">
                        <div>
                            <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-mm-muted">
                                Email address
                            </Label>
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                autoFocus
                                autoComplete="username"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-mm-muted">
                                Password
                            </Label>
                            <Input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••"
                                autoComplete="current-password"
                                data-testid="login-password"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !email.trim() || !password}
                            className="mm-btn-primary w-full !py-3 disabled:opacity-60"
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> Signing in…</>
                            ) : (
                                <>Sign in <ArrowRight size={14} /></>
                            )}
                        </button>
                    </form>

                    {/* Forgot password — collapsed by default */}
                    <div className="mt-5 border-t border-mm-line pt-5">
                        {!showForgot ? (
                            <button
                                onClick={() => setShowForgot(true)}
                                className="text-sm font-medium text-mm-navy/80 hover:text-mm-blue hover:underline underline-offset-4"
                                data-testid="login-forgot-toggle"
                            >
                                Forgot password?
                            </button>
                        ) : (
                            <div data-testid="login-forgot-section">
                                <p className="text-xs text-mm-muted">
                                    Enter your email above, then send yourself a one-time reset link.
                                </p>
                                {forgotSent ? (
                                    <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                                        <CheckCircle2 size={16} />
                                        Reset link sent to {email}.
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleForgot}
                                        disabled={loading || !email.trim()}
                                        className="mm-btn-outline mt-3 w-full !py-2.5 disabled:opacity-60"
                                        data-testid="login-forgot-submit"
                                    >
                                        <Mail size={14} /> Send reset link
                                    </button>
                                )}
                                {forgotDevLink && (
                                    <button
                                        onClick={() => applyMagicLink(forgotDevLink)}
                                        disabled={loading}
                                        className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-900 hover:underline"
                                        data-testid="login-forgot-dev-link"
                                    >
                                        <ExternalLink size={12} /> Dev: open reset link
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dev-only magic link helper (fallback for AUTH_DEV_MODE) */}
                    {DEV_MODE && (
                        <div className="mt-5 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-3" data-testid="login-dev-helper">
                            <p className="text-xs font-medium uppercase tracking-wider text-amber-900">
                                Dev mode · magic-link bypass
                            </p>
                            {magicDevLink ? (
                                <button
                                    onClick={() => applyMagicLink(magicDevLink)}
                                    disabled={loading}
                                    className="mm-btn-outline mt-2 w-full !py-2 text-xs disabled:opacity-60"
                                    data-testid="login-dev-verify"
                                >
                                    <ExternalLink size={12} /> Use dev magic link
                                </button>
                            ) : (
                                <button
                                    onClick={handleDevMagic}
                                    disabled={loading || !email.trim()}
                                    className="mt-2 text-xs text-amber-900 hover:underline disabled:opacity-50"
                                >
                                    Generate dev magic link →
                                </button>
                            )}
                        </div>
                    )}
                </div>

                    {/* Right — Phase 12 Google signup card (soft prompt everywhere) */}
                    <div className="mm-card flex flex-col p-8" data-testid="login-google-card">
                        <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mm-sky/15 text-mm-blue">
                            <UserPlus size={18} />
                        </div>
                        <h2 className="mm-display text-2xl text-mm-navy">Create your account</h2>
                        <p className="mt-2 text-sm text-mm-muted">
                            Save your Jarvis chat and get updates from a licensed advisor.
                        </p>

                        <button
                            type="button"
                            onClick={handleGoogle}
                            disabled={googleLoading}
                            className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-mm-line bg-white px-5 py-3 text-sm font-medium text-mm-ink shadow-sm transition-colors hover:bg-mm-bg disabled:opacity-60"
                            data-testid="login-google-button"
                        >
                            {googleLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <GoogleGlyph />
                            )}
                            {googleLoading ? "Opening Google…" : "Continue with Google"}
                        </button>

                        <ul className="mt-6 space-y-2 text-xs text-mm-muted">
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-mm-sky" />
                                Save your Jarvis chat history across devices.
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-mm-sky" />
                                A licensed advisor can follow up with a personalized plan.
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-mm-sky" />
                                We never sell your data.
                            </li>
                        </ul>

                        <p className="mt-auto pt-6 text-[11px] leading-relaxed text-mm-muted">
                            Powered by Emergent-managed Google sign-in. We never store your Google password.
                        </p>
                    </div>
                </div>
                <p className="mt-6 text-center text-xs text-mm-muted">
                    Staff only on the left. Visitors — use Google sign-in on the right, or head back to{" "}
                    <a href="/" className="mm-link">movingmountainsinc.com</a>.
                </p>
            </div>
        </div>
    );
}

/* Tiny inline Google "G" mark so we don't ship a brand asset download for one page. */
function GoogleGlyph() {
    return (
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            <path fill="none" d="M0 0h48v48H0z" />
        </svg>
    );
}
